import { Item, SpecialAbility, select } from "gamedata/GameData";
import { ReactElement, ReactNode, useState } from "react";
import { ItemLink } from "~/components/item/ItemLink";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { PageTitle } from "~/components/ui/PageTitle";
import { Table, renderTableTr } from "~/components/ui/Table";
import { tt, useRealm, useT, useTurl } from "~/util/Lang";
import { $LOADER, $PAGE, OurLoaderArgs } from "~/util/Loaders";
import { homePage } from "~/util/titles";

import css from "./sa.module.css";
import { Link } from "react-router";
import {
  urlQueryFromSearchParams,
  useSetUrlQuery,
  useUrlQuery,
} from "~/util/UrlUtils";
import { ControlBox } from "~/components/form/ControlBox";
import { MultiSelect } from "~/components/form/MultiSelect";
import {
  GRADES,
  GRADE_NAMES,
  Grade,
  WEAPON_TYPES_BY_GAME_VER,
  WEAPON_TYPE_NAMES,
  WeaponType,
} from "~/types";
import { CrystalColor } from "~/components/item/Crystal";
import { createFiltering } from "~/util/Filtering";
import { Button } from "~/components/ui/Button";
import { createSorting } from "~/util/Sorting";
import { logPageView } from "~/util/stats";

type SAFilter = {
  grade: readonly Grade[];
  weap_type: readonly WeaponType[];
};

type Config = {
  noArmorType?: boolean;
  noWeaponType?: boolean;
  filter?: (item: SpecialAbility) => boolean;
};

const FILTER = createFiltering<SAFilter, SpecialAbility>({
  pack: (f) => ({
    grade: f.grade?.join(","),
    wt: f.weap_type.join(","),
  }),
  unpack: (r) => {
    const grade = Grade.array().parse(r.grade ? r.grade.split(",") : []);
    const weap_type = WeaponType.array().parse(r.wt?.split(",") ?? []);
    return {
      grade,
      weap_type,
    };
  },
  filter: ({ item }, f) => {
    if (f.grade.length > 0 && !f.grade.includes(item.crystalType as any)) {
      return false;
    }
    if (f.weap_type.length > 0 && !f.weap_type.includes(item.weaponType)) {
      return false;
    }
    return true;
  },
});

async function makeData(la: OurLoaderArgs, cfg: Config) {
  // Log
  logPageView(la);
  //
  const t = tt.bind(null, la.realm.lang);

  const filter = FILTER.fromUrlQuery(
    urlQueryFromSearchParams(la.url.searchParams)
  );

  const all = Array.from(la.gd.sa.values());
  const filtered = FILTER.apply(all, filter, cfg.filter);

  return {
    authUser: la.req.sess?.user,
    sa: select(filtered, {
      select: {
        item: {
          id: true,
          itemName: true,
          icon: true,
          weaponType: true,
          name: t,
          crystalType: true,
        },
        level: true,
        abilities: {
          saBlue: {
            id: true,
            itemName: true,
            icon: true,
            name: t,
            addName: t,
            crystalType: true,
          },
          saRed: {
            id: true,
            itemName: true,
            icon: true,
            name: t,
            addName: t,
            crystalType: true,
          },
          saGreen: {
            id: true,
            itemName: true,
            icon: true,
            name: t,
            addName: t,
            crystalType: true,
          },
        },
      },
    }),
  };
}

$PAGE({
  path: "sa",
  loader: $LOADER?.(async (_la) => {
    return makeData(_la, {});
  }),
  meta: (cx) => {
    const title = "Special Abilities";

    return {
      title: `${title} - ${homePage(cx.realm)}`,
      description: "Special Abilities",
    };
  },
  render: ({ data: _ }) => {
    const t = useT();
    const turl = useTurl();
    const classesTitle = t({
      en: "Special Abilities",
      ru: "Специальные бонусы оружия",
    });
    const breadcrumbList = [{ title: classesTitle, url: turl("/sa") }];

    const rows: Array<(typeof _.sa)[number] | ReactElement> = [];

    _.sa.sort((a, b) => a.level - b.level);

    const grouped = _.sa.reduce((groups, item) => {
      (groups[item.item.weaponType] ||= []).push(item);
      return groups;
    }, {} as Record<string, typeof _.sa>);

    let lastWeaponType = "";
    for (const [key, values] of Object.entries(grouped)) {
      if (key !== lastWeaponType) {
        rows.push(
          renderTableTr(
            { key: "type-" + key },
            <td colSpan={5} className={css.weaponType}>
              {t({ en: "Type:", ru: "Тип:" })} {key}
            </td>
          )
        );
        lastWeaponType = key;
      }
      for (const v of values) {
        rows.push(v);
      }
    }

    return (
      <div>
        <div className="content">
          <Breadcrumb items={breadcrumbList} />
          {/* {JSON.stringify(_.sa)} */}
          <PageTitle>{classesTitle}</PageTitle>
          <Filters />
        </div>
        <div style={{ overflowX: "auto" }}>
          <Table
            data={rows}
            id={(_sa, i) => i}
            cols={[
              {
                title: "",
                cell: ({ item }) => <ItemLink item={item} />,
                thPadding: "0",
                padding: "8px 0 8px 8px",
                width: 32 + 12,
              },
              {
                title: "SA Lvl",
                cell: ({ level }) => level,
              },
              {
                title: (
                  <span style={{ color: CrystalColor("b") }}>Red Crystal</span>
                ),
                cell: ({ abilities }) => (
                  <Link to={turl(`/items/${abilities.saRed.itemName}`)}>
                    {abilities.saRed.addName}
                  </Link>
                ),
              },
              {
                title: (
                  <span style={{ color: CrystalColor("c") }}>
                    Green Crystal
                  </span>
                ),
                cell: ({ abilities }) => (
                  <Link to={turl(`/items/${abilities.saGreen.itemName}`)}>
                    {abilities.saGreen.addName}
                  </Link>
                ),
              },
              {
                title: (
                  <span style={{ color: CrystalColor("d") }}>Blue Crystal</span>
                ),
                cell: ({ abilities }) => (
                  <Link to={turl(`/items/${abilities.saBlue.itemName}`)}>
                    {abilities.saBlue.addName}
                  </Link>
                ),
              },
            ]}
          />
        </div>
      </div>
    );
  },
});

const asWeaponTypes = WeaponType.array().parse;

function Filters(props: { pages?: ReactNode }) {
  const realm = useRealm();
  const t = useT();
  const urlQuery = useUrlQuery();
  const setUrlQuery = useSetUrlQuery();

  const [filter, setFilter] = useState(() => FILTER.fromUrlQuery(urlQuery));

  return (
    <div style={{ padding: "0 5px" }}>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        <ControlBox
          title="Grade"
          reset={() => setFilter((f) => ({ ...f, grade: [] }))}
        >
          <MultiSelect
            options={GRADES.map((grade) => ({
              value: grade,
              title: GRADE_NAMES[grade].en,
              color: CrystalColor(grade),
            }))}
            value={filter.grade}
            onChange={(grade) =>
              setFilter((f) => ({ ...f, grade: Grade.array().parse(grade) }))
            }
          />
        </ControlBox>
        <ControlBox
          title="Weapon type"
          reset={() => setFilter((f) => ({ ...f, weap_type: [] }))}
        >
          <MultiSelect
            options={WEAPON_TYPES_BY_GAME_VER[realm.game].map((wt) => ({
              value: wt,
              title: t(WEAPON_TYPE_NAMES[wt]),
            }))}
            value={filter.weap_type}
            onChange={(wt) =>
              setFilter((f) => ({ ...f, weap_type: asWeaponTypes(wt) }))
            }
          />
        </ControlBox>
      </div>

      <div
        style={{
          alignItems: "flex-end",
          display: "flex",
          justifyContent: "space-between",
          paddingTop: 20,
        }}
      >
        <div style={{ padding: "0 0 8px" }}>
          <Button
            onClick={() => {
              setUrlQuery(
                { ...urlQuery, f: FILTER.encode(filter) },
                { preventScrollReset: true }
              );
            }}
          >
            Apply
          </Button>
        </div>
        {props.pages}
      </div>
    </div>
  );
}
